import React, { Component } from 'react';
import utils from 'utils';
import { uuid } from 'utils/dom';
import cn from 'classnames';
import { ANALYTICS, TOOLTIP_THEMES } from 'constants';
import { capitalize } from 'utils/string';
import Button from 'components/Button';
import TooltipTrigger from './TooltipTrigger';
import ToolTipTemplate from './ToolTipTemplate';
import { propTypes, defaultProps } from './ToolTipPropType';

/**
 * Portal to render mobile tooltips into body
 */
const themeClasses = {
  [TOOLTIP_THEMES.LIGHT_BLUE]: 'tooltips__info-icon--light-blue-theme',
  [TOOLTIP_THEMES.REGULAR_SLATE]: 'tooltips__info-icon--slate-theme',
};

// reusable trigger button
const ToolTipTriggerButton = React.forwardRef(
  ({ triggerTitle, tooltipID, className, ariaLabel, onClick, ...props }, ref) => (
    <Button
      plain
      type='button'
      aria-describedby={tooltipID}
      title={triggerTitle}
      aria-label={ariaLabel}
      className={className}
      onClick={onClick}
      {...props}
      buttonRef={ref}
    />
  )
);

/*
 * Using https://github.com/mohsinulhaq/react-popper-tooltip
 * Placements for tooltips =>
 * https://github.com/mohsinulhaq/react-popper-tooltip#placement
 */
class ToolTip extends Component {
  constructor(props) {
    super(props);
    // This is for accessibility -> aria-describedby
    const tooltipUniqueID = uuid();
    this.state = {
      showToolTip: false,
      tooltipID: `tooltip-id-${tooltipUniqueID}`,
    };

    this.closeButtonRef = React.createRef();
  }

  static currentlyOpenTooltip = null;

  static propTypes = propTypes;

  static defaultProps = defaultProps;

  handleAnalytics = () => {
    const { analyticsKey } = this.props;

    if (analyticsKey) {
      // Analytics - fire custom "modal" event (push to interaction object)
      utils.analytics.interaction(
        capitalize(ANALYTICS.UI_TOOLTIP), // type
        analyticsKey, // key
        this.state.showToolTip ? ANALYTICS.CLOSE : ANALYTICS.OPEN // value
      );
    }
  };

  handleClickOutside = (e) => {
    const tooltipEl = document.getElementById(this.state.tooltipID);
    if (tooltipEl && !tooltipEl.contains(e.target)) {
      this.closeModal(e);
    }
  };

  handleClick = (e) => {
    this.handleAnalytics();
    e.stopPropagation?.();

    // Close any previously open tooltip
    if (ToolTip.currentlyOpenTooltip && ToolTip.currentlyOpenTooltip !== this) {
      ToolTip.currentlyOpenTooltip.closeModal();
    }

    const willShow = !this.state.showToolTip;

    this.setState({ showToolTip: willShow }, () => {
      // Wait for tooltip to render, then focus the close button
      if (willShow) {
        ToolTip.currentlyOpenTooltip = this;
        document.addEventListener('click', this.handleClickOutside);
        setTimeout(() => {
          if (this.closeButtonRef.current) {
            this.closeButtonRef.current.focus();
          }
        }, 50); // Small delay to allow render
      } else {
        // Tooltip just closed
        ToolTip.currentlyOpenTooltip = null;
        document.removeEventListener('click', this.handleClickOutside);
      }
    });
  };

  closeModal = (e) => {
    this.handleAnalytics();

    if (e) e.stopPropagation?.();

    this.setState({ showToolTip: false }, () => {
      if (ToolTip.currentlyOpenTooltip === this) {
        ToolTip.currentlyOpenTooltip = null;
      }
      document.removeEventListener('click', this.handleClickOutside);
    });
  };

  handleClickMainModal = (e) => {
    // When someone clicks the modal we don't want the event to bubble up and trigger other click handlers
    e.stopPropagation();
  };

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('click', this.handleClickOutside);
    if (ToolTip.currentlyOpenTooltip === this) {
      ToolTip.currentlyOpenTooltip = null;
    }
  }

  handleKeyDown = (e) => {
    const { showToolTip } = this.state;
    const key = utils.accessibility.formatKeys(e);

    if (key.escape && showToolTip) {
      this.closeModal();
    }

    if (key.tab && showToolTip) {
      e.preventDefault();

      // Close the tooltip first
      this.closeModal();

      setTimeout(() => {
        // Get all tabbable elements
        const tabbables = Array.from(
          document.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => !el.disabled && el.offsetParent !== null); // visible & enabled

        const currentIndex = tabbables.indexOf(this.triggerButtonRef);
        const nextIndex = key.shift ? currentIndex - 1 : currentIndex + 1;
        const next = tabbables[nextIndex];

        if (next) next.focus();
      }, 0);
    }
  };

  toolTipHeader = () => (
    <Button
      plain
      type='button'
      className='tooltips__button-toggle'
      aria-label={utils.i18n('close_tooltip')}
      title={utils.i18n('close_tooltip')}
      tabIndex='-1'
      onClick={this.handleClick}
      buttonRef={this.closeButtonRef}
    />
  );

  handleBlur = (e) => {
    setTimeout(() => {
      const focused = document.activeElement;
      const tooltipEl = document.getElementById(this.state.tooltipID);
      if (!tooltipEl || !tooltipEl.contains(focused)) {
        this.closeModal(e);
      }
    }, 0); // wait for next focus shift
  };

  render() {
    const { breakpoint, children, placement, theme, triggerTitle, interactive, followCursor } = this.props;
    const { showToolTip, tooltipID } = this.state;
    const themeClass = themeClasses[theme];

    return (
      <TooltipTrigger
        // Where the tooltip should be positioned
        placement={breakpoint.isMobile ? 'auto' : placement}
        // How the tooltip should be triggered
        trigger='click'
        tooltipShown={showToolTip}
        // Passing tooltipHeader down so we can close the modal by clicking on the button
        // children will be the content of the modal
        tooltip={ToolTipTemplate(this.toolTipHeader(), children, tooltipID)}
        // for accessibility
        tooltipID={tooltipID}
        interactive={interactive}
        followCursor={followCursor}
      >
        {({ triggerRef }) => (
          <ToolTipTriggerButton
            tooltipID={tooltipID}
            onBlur={this.handleBlur}
            title={triggerTitle}
            ariaLabel={triggerTitle}
            ref={(el) => {
              this.triggerButtonRef = el;
              triggerRef(el);
            }}
            onClick={this.handleClick}
            className={cn('tooltips__info-icon', themeClass)}
          />
        )}
      </TooltipTrigger>
    );
  }
}

export default ToolTip;
